import { notification } from 'antd';
import _ from 'lodash';

import { bindServices } from '@/services/api';
import { CoreEnums } from '@/utils/domain/constants';
import { toPaginate } from '@/utils/infrastructure';

const api = bindServices('manageEntity');

const meta = { page: 1, limit: 5, count: 0, totalPages: 0 };

const Model = {
  namespace: 'manageEntity',
  state: {
    pagination: meta,
    searchCriteria: '',
    view: false,
    orderBy: '',
    entity: null,
    fields: [],
    data: [],
  },
  effects: {
    *fetchEntityMetadata({ payload }, { call, put }) {
      try {
        const res = yield call(api.getById, { route: 'dynamicEntity', ...payload });
        yield put({ type: 'queryEntityMetadata', payload: res });
        return true;
      } catch (e) {
        return false;
      }
    },

    *fetchEntityFields({ payload }, { call, put }) {
      try {
        const data = yield call(api.getById, {
          route: 'dynamicEntity/config/published/fields',
          ...payload,
        });
        yield put({ type: 'queryEntityFields', payload: data });
      } catch (e) {
        yield put({ type: 'queryEntityFields', payload: [] });
      }
    },

    *fetchEntityData({ payload }, { call, put }) {
      try {
        const res = yield call(api.getById, { route: 'entity/getData', ...payload });
        yield put({
          type: 'queryEntityData',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {
        yield put({
          type: 'queryEntityData',
          payload: { items: [], totalCount: 0 },
        });
      }
    },

    *saveEntityData({ payload }, { call, put }) {
      try {
        const { id, itemId, data } = payload;
        const route = `entity/${id}`;

        if (itemId) yield call(api.update, { route: `${route}/${itemId}`, method: 'patch', data });
        else yield call(api.create, { route, data });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'querySaveData' });
      } catch (err) {
        if (err.message) {
          const { message, validationErrors } = err;
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message },
            validationErrors,
          });
        }
      }
    },

    *fetchEntityItem({ payload }, { call, put }) {
      try {
        const { entityId, itemId } = payload;
        const res = yield call(api.apiRoute, {
          route: `entity/getData/${entityId}/item/${itemId}`,
        });
        yield put({ type: 'queryEntityItem', payload: res.item1 ?? res });
      } catch (e) {}
    },

    *deleteItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'global/modalConfirm', state: false, id: null });
        yield call(api.apiRoute, {
          route: `entity/${payload.entityId}/${payload.id}`,
          method: 'delete',
        });
        yield put({ type: 'queryDeleteEntity' });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (err) {
        if (err.message) {
          yield put({
            type: `global/showNotifications${view ? 'Form' : 'Global'}`,
            payload: {
              visible: true,
              type: 'error',
              message: err.message,
              autoHide: view,
            },
          });
        }
      }
    },

    *exportEntityData({ payload }, { call }) {
      try {
        const res = yield call(api.apiRoute, {
          route: `entity/export/${payload.entityId}`,
          data: {},
        });

        if (res.message) {
          notification.success({
            description: res.message,
            message: '',
            duration: 15,
          });
        }
      } catch (e) {}
    },

    *import({ payload }, { call }) {
      try {
        const res = yield call(api.apiRoute, {
          method: 'post',
          route: `entity/import/${payload.id}`,
          data: payload.data,
        });

        if (res.message) {
          notification.success({
            message: '',
            description: res.message,
            duration: 15,
          });
        }
      } catch (e) {}
    },

    *isActive({ payload }, { call, put }) {
      try {
        yield call(api.activate, { route: 'entity', ...payload });
        yield put({ type: 'queryActivate' });
      } catch (e) {}
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },
  },
  reducers: {
    queryEntityMetadata(state, { payload }) {
      return {
        ...state,
        entity: payload,
        executedCommand: null,
        readPriority: null,
      };
    },

    queryEntityFields(state, { payload }) {
      const data = payload?.items ?? payload;
      const orderedBy = _.orderBy(data, ['order', 'displayText'], ['asc', 'asc']);

      return {
        ...state,
        executedCommand: null,
        readPriority: null,
        fields: orderedBy,
      };
    },

    queryEntityData(state, { payload }) {
      return {
        ...state,
        data: payload.items,
        executedCommand: null,
        readPriority: null,
        pagination: {
          ...state.pagination,
          ...toPaginate(payload),
        },
      };
    },

    querySaveData(state) {
      return {
        ...state,
        executedCommand: CoreEnums.EntityQueryOperationType.FieldForm,
        readPriority: true,
      };
    },

    queryEntityItem(state, { payload }) {
      return {
        ...state,
        entityItem: payload,
      };
    },

    queryDeleteEntity(state) {
      return {
        ...state,
        executedCommand: CoreEnums.EntityQueryOperationType.FieldForm,
        readPriority: true,
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryActivate(state) {
      return {
        ...state,
        executedCommand: CoreEnums.EntityQueryOperationType.FieldForm,
        readPriority: true,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
      };
    },

    hideForms(state) {
      return {
        ...state,
        executedCommand: null,
        orderBy: '',
      };
    },

    resetState() {
      return initialState;
    },
  },
};

export default Model;
